import React, { useEffect } from 'react';
import { BackHandler, Pressable, Modal as RNModal, View } from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import Animated, {
  FadeIn,
  FadeOut,
  SlideInDown,
  SlideOutDown,
  useAnimatedStyle,
  useSharedValue,
  withTiming
} from 'react-native-reanimated';
interface ModalProps {
  visible: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  animationType?: 'fade' | 'slide' | 'none';
  backdrop?: boolean;
  backdropPress?: boolean;
  testID?: string;
}

export function Modal({
  visible,
  onClose,
  animationType = 'fade',
  children,
  backdrop = true,
  backdropPress = true,
  className = '',
  testID,
  ...props
}: ModalProps) {
  const backdropOpacity = useSharedValue(0);

  // Handle Android back button
  useEffect(() => {
    const backHandler = BackHandler.addEventListener('hardwareBackPress', () => {
      if (visible) {
        onClose();
        return true;
      }
      return false;
    });

    return () => backHandler.remove();
  }, [visible, onClose]);

  // Backdrop animation
  const backdropStyle = useAnimatedStyle(() => {
    return {
      opacity: backdropOpacity.get(),
    };
  });

  useEffect(() => {
    if (visible) {
      backdropOpacity.set(withTiming(1, { duration: 200 }));
    } else {
      backdropOpacity.set(withTiming(0, { duration: 200 }));
    }
  }, [visible, backdropOpacity]);

  const handleBackdropPress = () => {
    if (backdropPress) {
      onClose();
    }
  };

  // Animation variants
  const getEnteringAnimation = () => {
    switch (animationType) {
      case 'slide':
        return SlideInDown.duration(300);
      case 'fade':
        return FadeIn.duration(200);
      default:
        return undefined;
    }
  };

  const getExitingAnimation = () => {
    switch (animationType) {
      case 'slide':
        return SlideOutDown.duration(250);
      case 'fade':
        return FadeOut.duration(150);
      default:
        return undefined;
    }
  };

  if (!visible) {
    return null;
  }

  return (
    <RNModal
      transparent
      visible={visible}
      onRequestClose={onClose}
      statusBarTranslucent
      animationType="none"
      testID={testID}
      {...props}
    >
      <View style={{ flex: 1 }}>
        {/* Backdrop */}
        {backdrop && (
          <Animated.View
            style={[
              {
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                zIndex: 1,
              },
              backdropStyle,
            ]}
          >
            <Pressable
              style={{ flex: 1 }}
              onPress={handleBackdropPress}
              accessibilityLabel="Close modal"
              accessibilityRole="button"
            />
          </Animated.View>
        )}

        {/* Modal Content Container. KeyboardAvoidingView lifts the centered card
            above the keyboard so an input inside it stays visible when focused. */}
        <KeyboardAvoidingView
          behavior="padding"
          style={{
            flex: 1,
            justifyContent: 'center',
            alignItems: 'center',
            padding: 16,
            zIndex: 2,
          }}
          pointerEvents="box-none"
        >
          <Animated.View
            entering={getEnteringAnimation()}
            exiting={getExitingAnimation()}
            pointerEvents="auto"
            style={{
              backgroundColor: 'transparent',
              borderRadius: 12,
              maxWidth: 400,
              width: '100%',
              maxHeight: '80%',
              overflow: 'hidden',
            }}
            className={className}
          >
            {children}
          </Animated.View>
        </KeyboardAvoidingView>
      </View>
    </RNModal>
  );
}

// Modal sub-components for better composition
Modal.Header = function ModalHeader({
  children,
  className = '',
  ...props
}: {
  children: React.ReactNode;
  className?: string;
  [key: string]: any;
}) {
  return (
    <View
      style={{
        padding: 24,
        paddingBottom: 16,
      }}
      className={`border-b border-border ${className}`}
      {...props}
    >
      {children}
    </View>
  );
};

Modal.Content = function ModalContent({
  children,
  className = '',
  ...props
}: {
  children: React.ReactNode;
  className?: string;
  [key: string]: any;
}) {
  return (
    <View style={{ padding: 24 }} className={className} {...props}>
      {children}
    </View>
  );
};

Modal.Footer = function ModalFooter({
  children,
  className = '',
  ...props
}: {
  children: React.ReactNode;
  className?: string;
  [key: string]: any;
}) {
  return (
    <View
      style={{
        padding: 24,
        paddingTop: 16,
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 8,
      }}
      className={`border-t border-border ${className}`}
      {...props}
    >
      {children}
    </View>
  );
};

export default Modal;
